import { CssBaseline, StyledEngineProvider, ThemeProvider } from '@mui/material';

// Suppress benign ResizeObserver errors that occur during drag-and-drop operations
const resizeObserverError = (e: ErrorEvent) => {
    if (e.message?.includes('ResizeObserver loop') || e.message?.includes('ResizeObserver loop completed')) {
        e.stopImmediatePropagation();
        e.preventDefault();
        return false;
    }
    return true;
};
window.addEventListener('error', resizeObserverError);

window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    if (e.reason?.message?.includes('ResizeObserver')) {
        e.preventDefault();
    }
});

import { Provider as JotaiProvider } from 'jotai';
import { lazy, StrictMode, Suspense, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, useLocation, useRoutes } from 'react-router-dom';
import { jotaiStore } from '@/atoms/store';
import { useCommentsDrawer } from '@/comments';
import { Mode } from '@/enums';
import { useColorMode } from '@/hooks';
import { routes } from '@/routes';
import '@/styles/tailwind.css';
import { theme } from '@/theme';
import '@/utils/i18n';
import { QueryClientProvider } from '@tanstack/react-query';
import { FlagProvider, UnleashClient, useUnleashContext } from '@unleash/proxy-client-react';
import { useUserQuery } from '@/api/queries/useAuth';
import { queryClient } from '@/config/queryClient';

const ReactQueryDevtools =
    import.meta.env.DEV && !import.meta.env.VITE_E2E
        ? lazy(() =>
              import('@tanstack/react-query-devtools').then(m => ({
                  default: m.ReactQueryDevtools,
              })),
          )
        : () => null;

// Defer Sentry initialization until after first paint.
// Never in dev: replayIntegration (rrweb) serializes DOM mutations on every
// keystroke (~50ms/keypress in a large script, measured 2026-07-10) and
// browserProfilingIntegration adds V8 profiler interrupts to every task —
// together they dwarf the editor's own frame budget.
const initSentry = () =>
    import.meta.env.DEV
        ? Promise.resolve()
        : import('@sentry/react').then(Sentry => {
              Sentry.init({
                  dsn: 'https://1fb93bd0e20549b14f2378b927fb4513@o4505641432580096.ingest.sentry.io/4505690942996480',
                  integrations: [Sentry.browserProfilingIntegration(), Sentry.replayIntegration()],
                  tracesSampleRate: 1.0,
                  replaysSessionSampleRate: 0.1,
                  replaysOnErrorSampleRate: 1.0,
              });
          });

const config = {
    url: 'https://kokian.ru/proxy',
    clientKey: 'igV2p<fB7',
    refreshInterval: 150,
    appName: 'storyliner',
    disableMetrics: true,
    bootstrap: import.meta.env.DEV
        ? [
              {
                  name: 'pro',
                  enabled: true,
                  variant: { name: 'disabled', enabled: false },
                  impressionData: false,
              },
          ]
        : undefined,
    bootstrapOverride: false,
};

const client = new UnleashClient(config);

const WithContexts = () => {
    const element = useRoutes(routes);
    const _location = useLocation();
    const [, drawerActions] = useCommentsDrawer();
    const isDarkMode = useColorMode();
    const mode = isDarkMode ? Mode.dark : Mode.light;
    const updateContext = useUnleashContext();
    const sentryInitialized = useRef(false);

    useEffect(() => {
        drawerActions.close();
    }, [drawerActions]);

    // Sync Tailwind dark mode class with MUI theme mode
    useEffect(() => {
        document.documentElement.classList.toggle('dark', isDarkMode);
    }, [isDarkMode]);

    // Initialize Sentry after first paint
    useEffect(() => {
        if (!sentryInitialized.current) {
            sentryInitialized.current = true;
            (window.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 1)))(() => {
                initSentry();
            });
        }
    }, []);

    const { data: user } = useUserQuery();

    useEffect(() => {
        async function init() {
            if (!user) return;
            await updateContext({ userId: user.email });
            client.start();
        }
        init();
    }, [user?.email, user, updateContext]);

    return (
        // enableCssLayer wraps all MUI/sx/styled() emotion output in `@layer mui`;
        // only works with the layer-order pin at the top of styles/tailwind.css.
        // See docs/adr/0017-mui-css-cascade-layer.md.
        <StyledEngineProvider enableCssLayer>
            <ThemeProvider theme={theme(mode)}>
                <CssBaseline>{element}</CssBaseline>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

const App = () => {
    return (
        <StrictMode>
            <QueryClientProvider client={queryClient}>
                <FlagProvider unleashClient={client} startClient={false}>
                    <BrowserRouter>
                        <JotaiProvider store={jotaiStore}>
                            <WithContexts />
                        </JotaiProvider>
                    </BrowserRouter>
                </FlagProvider>
                <Suspense>
                    <ReactQueryDevtools initialIsOpen={false} />
                </Suspense>
            </QueryClientProvider>
        </StrictMode>
    );
};

createRoot(document.querySelector('.app') ?? document.body).render(<App />);
